import FetcherFactory from 'Shared/FetcherFactory'
import { getAuthenticityToken, stripInvisible } from 'Shared/helpers'

export const reducerName = 'SESSION_SAVER'

/**
 * {
 *   id: CLONE_LEAGUE_ID,
 *   cloningStep: 2,
 *   leagues: {
 *    'SOURCE_LEAGUE_ID_1': {
 *      'new_name': 'My Cool League',
 *      'should_clone_rounds': true,
 *      'new_round_dates': [...],
 *      'should_clone_players': false,
 *    },
 *    'SOURCE_LEAGUE_ID_2': {
 *      'new_name': 'Weekend Warriors'
 *      // Similar to above
 *    }
 *   }
 * }
 */
const factory = new FetcherFactory(
  reducerName,
  ({ params, state }) => {
    const { cloneLeaguesSessionID, data } = params

    const body = {
      clone_leagues_session_id: cloneLeaguesSessionID,
      data: data,
    }

    return fetch(state.baseURL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': getAuthenticityToken(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then(response => {
      return response.json().then(({ outdatedLeagues, cloneLeaguesSession, ...rest }) => ({
        data: {
          cloneLeaguesSession: cloneLeaguesSession ?? rest,
          outdatedLeagues,
        },
        status: response.status,
      }))
    })
  },
  {
    ignoreParamsCaching: true,
  },
)

const SessionSaverReducer = factory.getReducer()
export const namedReducer = { [reducerName]: SessionSaverReducer }

export const getInitialState = ({
  cloneLeaguesSession,
  upsertCloneLeaguesSessionURL,
  editCloneLeaguesSessionURL,
  indexCloneLeaguesSessionsURL,
  customerDateFormat,
}) => {
  return {
    data: {
      cloneLeaguesSession: cloneLeaguesSession,
    },
    baseURL: upsertCloneLeaguesSessionURL,
    editURL: editCloneLeaguesSessionURL,
    indexURL: indexCloneLeaguesSessionsURL,
    customerDateFormat: customerDateFormat,
  }
}

export const selectCustomerDateFormat = state => state[factory.name]?.customerDateFormat
export const selectCloneLeaguesSession = state => state[factory.name]?.data?.cloneLeaguesSession
export const selectCloneLeaguesSessionID = state => state[factory.name]?.data?.cloneLeaguesSession?.id
export const selectCloneLeaguesEditURLTemplate = state => state[factory.name]?.editURL
export const selectCloneLeaguesIndexURL = state => state[factory.name]?.indexURL
export const selectCloneLeaguesSessionData = state => state[factory.name]?.data?.cloneLeaguesSession?.data
export const selectSessionSaverLoading = state => state[factory.name]?.loading
export const selectSessionSaverError = state => state[factory.name]?.error

export const saveSession = factory.getFetchAction()
export const setSessionSaverData = factory.getSetDataAction()

export const selectAllLeaguesHaveFullDates = (state) => {
  const leagues = selectCloneLeaguesSessionData(state)?.leagues
  if (!leagues) {
    return false
  }

  const entries = Object.values(leagues)
  if (!entries.length) {
    return false
  }

  return entries.every(league => {
    // Prefer rounds.length (the serialized rounds array, same source the UI and addDateToMassDates
    // guard use) over total_rounds. total_rounds comes from a separate DB count query that can
    // diverge from the association-loaded rounds array when the association has a default scope.
    const roundsCount = league?.rounds?.length ?? league?.total_rounds ?? 0
    const massDatesCount = league?.mass_dates?.length ?? 0
    return massDatesCount === roundsCount
  })
}

export const selectAllLeaguesRenamed = (state) => {
  const leagues = selectCloneLeaguesSessionData(state)?.leagues
  if (!leagues) {
    return false
  }

  const entries = Object.values(leagues)
  if (!entries.length) {
    return false
  }

  return entries.every(league =>
    league.league_name && stripInvisible(league.league_name) !== stripInvisible(league.name),
  )
}

export const selectAnyLeagueHasEmptyRename = (state) => {
  const leagues = selectCloneLeaguesSessionData(state)?.leagues
  if (!leagues) return false

  return Object.values(leagues).some(league =>
    stripInvisible(league.league_name ?? '').length === 0,
  )
}

export const addLeaguesToSession = (leaguesDict) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const newLeagues = Object.entries(leaguesDict).reduce((acc, [ id, data ]) => {
    if (!acc[id]) {
      acc[id] = { ...data }
    }
    return acc
  }, { ...existingLeagues })

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: newLeagues },
    },
  }))
}

export const removeLeaguesFromSession = (leagueIds) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const filtered = Object.keys(existingLeagues)
    .filter(id => !leagueIds.includes(id))
    .reduce((acc, id) => {
      acc[id] = existingLeagues[id]
      return acc
    }, {})

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: filtered },
    },
  }))
}

export const updateLeagueConfig = (leagueId, configs) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: {
        ...session?.data,
        leagues: {
          ...existingLeagues,
          [leagueId]: { ...existingLeagues[leagueId], ...configs },
        },
      },
    },
  }))
}

export const syncEnhancedLeaguesToSession = (enhancedLeagues) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const enhancedByID = enhancedLeagues.reduce((acc, league) => {
    acc[String(league.id)] = league
    return acc
  }, {})

  const updatedLeagues = Object.entries(existingLeagues).reduce((acc, [ id, league ]) => {
    const enhanced = enhancedByID[id]
    acc[id] = enhanced
      ? { ...league, rounds: enhanced.rounds, first_round: enhanced.first_round, last_round: enhanced.last_round }
      : league
    return acc
  }, {})

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: updatedLeagues },
    },
  }))
}

export const syncSelectedLeaguesToSession = (selectedLeagues) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const currentLeagueIds = Object.keys(session?.data?.leagues || {})
  const selectedLeagueIds = selectedLeagues.map(league => String(league.id))

  const toRemove = currentLeagueIds.filter(id => !selectedLeagueIds.includes(id))
  const toAdd = selectedLeagues
    .filter(league => !currentLeagueIds.includes(String(league.id)))
    .reduce((acc, league) => {
      acc[String(league.id)] = { name: league.name, id: String(league.id), league_name: league.name, ...league }
      return acc
    }, {})

  if (toRemove.length) {
    removeLeaguesFromSession(toRemove)(dispatch, getState)
  }

  if (Object.keys(toAdd).length) {
    addLeaguesToSession(toAdd)(dispatch, getState)
  }
}

export const stripAllLeagueNames = () => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const updatedLeagues = Object.entries(existingLeagues).reduce((acc, [ id, league ]) => {
    acc[id] = {
      ...league,
      league_name: stripInvisible(league.league_name ?? ''),
    }
    return acc
  }, {})

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: updatedLeagues },
    },
  }))
}

export const updateAllLeaguesConfigs = (configs) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const updatedLeagues = Object.entries(existingLeagues).reduce((acc, [ id, league ]) => {
    acc[id] = { ...league, ...configs }
    return acc
  }, {})

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: updatedLeagues },
    },
  }))
}
